/* eslint-disable no-param-reassign */
import React, { useContext, useEffect, useState } from 'react';
import { ExperienceContext, useConfigService, useStore } from '@thd-nucleus/experience-context';
import proptypes from 'prop-types';
import {
  arrayOf as arrayOfType,
  bool as boolType,
  client,
  extend,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  useDataModel,
  useLazyDataModel
} from '@thd-nucleus/data-sources';
import { Link } from '@one-thd/sui-atomic-components';
import {
  dataModel as deliveryDataModel,
  DeliveryOptions
} from '@thd-olt-component-react/delivery-options';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { getShippingTemplate } from '../helpers/shipping-helper';
import {
  isBODFS,
  isBrioProduct,
  isCustomKitchenCabinetProduct,
  isPodShippingEqual,
  isSTH,
  getFSDBMProgram
} from '../helpers/pod-fulfillment-utils';
import { getStateFromZipcode } from '../helpers/zipcode-helper';
import { EmptyShippingMessage } from '../partials/shipping/EmptyShippingMessage';
import { PriorityDeliveryOptions } from '../partials/shipping/PriorityDeliveryOptions';
import { FSDBMMessage} from '../partials/shipping/FSDBMMessage';
import {
  getDeliveryZip,
  getCheckAvailabilityText,
  getCheckAvailabilityDate
} from '../../components/helper/utils';
import { renderDeliveryTemplates } from '../templates/deliveryTemplate';
import { getEdgeCase } from './fulfillmentPodHelper';

const { bool, string, object } = proptypes;

export const FulfillmentPodShipping = React.memo(({
  itemId, storeId: primaryStoreId, deliveryZip,
  useCustomTemplate, directData = {}, hasSameDayFilter, showBundleFulfillmentMessage,
  showFasterPodSpacing, isNarrowPod, showFSDBMMessage, isAppliancePod
}) => {
  const { storeId: secondaryStoreId } = useStore();
  const { customer } = useContext(ExperienceContext);
  const [product, setProduct] = useState(null);
  const [availabilityData, setAvailabilityData] = useState(null);
  const theme = useTheme(FulfillmentPodShipping);
  const enableApplianceDeliveryCharge = useConfigService('enableApplianceDeliveryCharge');
  const enableFreeDeliveryForExchange = useConfigService('enableFreeDeliveryForExchange');
  const applianceDeliveryThreshold = useConfigService('applianceDeliveryThreshold');
  const enableDoubleDeliveryMessage = !!useConfigService('enableDoubleDeliveryMessage');
  // hide fsdbm message if hideFasterDelivery is true
  const fsdbm = !theme?.props?.hideFasterDelivery && getFSDBMProgram(product);
  const hideFasterDelivery = theme?.props?.hideFasterDelivery || (showFSDBMMessage && fsdbm?.threshold);
  const hideDeliveryEta = theme?.props?.hideDeliveryEta;
  const isNewFulfillmentPodMessagingEnabled = !hideDeliveryEta && !!useConfigService('fs:enableNewFulfillmentPodMessaging');
  const enablePLPFasterDeliveryOption = !!useConfigService('fs:enablePLPFasterDeliveryOption');

  const { isExchangeCustomer } = useThdCustomer() || {};
  const isPuertoRicoZip = !!(getStateFromZipcode(getDeliveryZip()) === 'PR');
  const storeId = primaryStoreId || secondaryStoreId;

  const opts = {
    variables: {
      itemId,
      storeId,
      zipCode: deliveryZip
    },
    skip: !itemId || !!directData,
    ssr: false
  };

  const checkAvailabilityOptions = {
    ssr: false,
    fetchPolicy: 'no-cache'
  };

  const { data, loading, error } = useDataModel('product', opts);
  const productData = data?.product || directData;
  const [
    fetchCheckAvailability,
    { data: checkAvailabilityData, loading: isLoadingAvailability }
  ] = useLazyDataModel('checkAvailability', checkAvailabilityOptions);

  useEffect(() => {
    if (!checkAvailabilityData) return;

    const fulfillmentOptions = checkAvailabilityData?.checkAvailability?.fulfillmentOptions;
    const services = fulfillmentOptions?.[0]?.services;
    const locations = services?.[0]?.locations;

    let earliestAvailabilityDate = getCheckAvailabilityDate(services);

    const response = {
      DeliveryAvailabilityResponse: {
        deliveryAvailability: {
          zipCode: getDeliveryZip(),
          primaryStrNbr: locations?.[0]?.locationId,
          availability: services?.[0]?.deliveryAvailabilityDetail,
          earliestAvailabilityDate
        }
      }
    };

    setAvailabilityData(response);

  }, [checkAvailabilityData]);

  useEffect(() => {
    if (productData) {
      setProduct(productData);
      setAvailabilityData(null);
    }
  }, [productData]);

  const edgeCaseToReturn = getEdgeCase({
    data, error, isLoadingAvailability, loading, product
  });
  if (edgeCaseToReturn) return edgeCaseToReturn;
  const bundleFlag = !!product?.bundleFlag;
  const bundleItemsIds = product?.bundleItems?.map((item) => item.id) || [];

  const nullConditions = [
    product?.info?.productSubType?.name === 'HDQC',
    isBrioProduct(product),
    isCustomKitchenCabinetProduct(product),
    !!product?.installServices?.scheduleAMeasure
  ];

  if (nullConditions.some((nullCondition) => nullCondition)) {
    return null;
  }
  const applianceButtonText = getCheckAvailabilityText({ enablePLPFasterDeliveryOption });

  const checkAvailabilityButton = (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <Link
      data-component="checkAvailabilityButton"
      underline="always"
      onClick={() => {
        fetchCheckAvailability({
          variables: {
            itemIds: bundleFlag && bundleItemsIds.length > 0 ? bundleItemsIds : [itemId],
            zipCode: getDeliveryZip(),
            localStoreId: storeId
          }
        });
      }}
    >
      <span className="sui-text-sm">{applianceButtonText}</span>
    </Link>
  );

  const customRender = useCustomTemplate ? renderDeliveryTemplates : null;
  const ShippingComponent = getShippingTemplate(
    {
      availabilityData,
      product,
      customer,
      checkAvailabilityButton,
      isPuertoRicoZip,
      customRender,
      enableApplianceDeliveryCharge,
      enableFreeDeliveryForExchange,
      applianceDeliveryThreshold,
      isExchangeCustomer,
      bundleFlag,
      showBundleFulfillmentMessage,
      isNewFulfillmentPodMessagingEnabled,
      isNarrowPod,
      isAppliancePod
    }
  );
  if (!ShippingComponent) { return EmptyShippingMessage; }

  let BodfsComponent = null;
  const bodfsConditions = [
    enableDoubleDeliveryMessage,
    hasSameDayFilter,
    isSTH(product),
    isBODFS(product)
  ];
  if (bodfsConditions.every((condition) => !!condition)) {
    BodfsComponent = getShippingTemplate({
      availabilityData,
      product,
      customer,
      checkAvailabilityButton,
      isPuertoRicoZip,
      customRender,
      enableApplianceDeliveryCharge,
      enableFreeDeliveryForExchange,
      applianceDeliveryThreshold,
      isExchangeCustomer,
      hasSameDayFilter,
      isNewFulfillmentPodMessagingEnabled,
      isNarrowPod,
      isAppliancePod
    });
  }

  return (
    <span className="sui-leading-tight" data-component="FulfillmentPodShipping">
      <ShippingComponent
        availabilityData={availabilityData}
        product={product}
        checkAvailabilityButton={checkAvailabilityButton}
        isPuertoRicoZip={isPuertoRicoZip}
        enableApplianceDeliveryCharge={enableApplianceDeliveryCharge}
        enableFreeDeliveryForExchange={enableFreeDeliveryForExchange}
        isExchangeCustomer={isExchangeCustomer}
        bundleFlag={bundleFlag}
        showBundleFulfillmentMessage={showBundleFulfillmentMessage}
        isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
      />
      {BodfsComponent && (
        <BodfsComponent
          availabilityData={availabilityData}
          product={product}
          checkAvailabilityButton={checkAvailabilityButton}
          isPuertoRicoZip={isPuertoRicoZip}
          enableApplianceDeliveryCharge={enableApplianceDeliveryCharge}
          enableFreeDeliveryForExchange={enableFreeDeliveryForExchange}
          isExchangeCustomer={isExchangeCustomer}
          isNewFulfillmentPodMessagingEnabled={isNewFulfillmentPodMessagingEnabled}
          isNarrowPod
        />
      )}
      {showFSDBMMessage && fsdbm?.threshold && <FSDBMMessage threshold={fsdbm.threshold} />}
      {!hideFasterDelivery && enablePLPFasterDeliveryOption && (
        <PriorityDeliveryOptions
          product={product}
          showFasterPodSpacing={showFasterPodSpacing}
          isNarrowPod={isNarrowPod}
        />
      )}
    </span>
  );

}, isPodShippingEqual);

FulfillmentPodShipping.themeProps = {
  hideFasterDelivery: bool,
  hideDeliveryEta: bool
};

FulfillmentPodShipping.defaultThemeProps = {
  hideFasterDelivery: false,
  hideDeliveryEta: false
};

FulfillmentPodShipping.propTypes = {
  itemId: string.isRequired,
  storeId: string,
  useCustomTemplate: bool,
  deliveryZip: string,
  // eslint-disable-next-line react/forbid-prop-types
  directData: object,
  hasSameDayFilter: bool,
  showBundleFulfillmentMessage: bool,
  isNarrowPod: bool,
  showFSDBMMessage: bool,
  isAppliancePod: bool
};

FulfillmentPodShipping.defaultProps = {
  useCustomTemplate: false,
  storeId: undefined,
  deliveryZip: undefined,
  directData: null,
  hasSameDayFilter: false,
  showBundleFulfillmentMessage: false,
  isNarrowPod: false,
  showFSDBMMessage: false,
  isAppliancePod: false
};

const installServices = params({ storeId: stringType(), zipCode: stringType() }).shape({
  scheduleAMeasure: boolType()
});
if (installServices.skip) {
  installServices.skip('skipInstallServices', true);
}

const Service = shapeType({
  deliveryTimeline: stringType(),
  deliveryDates: shapeType({
    startDate: stringType(),
    endDate: stringType()
  }),
  deliveryCharge: stringType(),
  dynamicEta: shapeType({
    hours: stringType(),
    minutes: stringType()
  }),
  hasFreeShipping: boolType(),
  deliveryMessage: stringType(),
  freeDeliveryThreshold: numberType({ float: true }),
  earliestDeliveryDate: stringType(),
  locations: arrayOfType(shapeType({
    curbsidePickupFlag: boolType(),
    isBuyInStoreCheckNearBy: boolType(),
    distance: numberType({ float: true }),
    inventory: shapeType({
      isOutOfStock: boolType(),
      isInStock: boolType(),
      isLimitedQuantity: boolType(),
      isUnavailable: boolType(),
      quantity: numberType(),
      maxAllowedBopisQty: numberType(),
      minAllowedBopisQty: numberType()
    }),
    isAnchor: boolType(),
    locationId: stringType(),
    state: stringType(),
    storeName: stringType(),
    storePhone: stringType(),
    type: stringType()
  })),
  shipFromFastestLocation: boolType(),
  type: stringType(),
  totalCharge: numberType({ float: true }),
  optimalFulfillment: boolType()
});

FulfillmentPodShipping.dataModel = extend({
  product: params({
    itemId: stringType().isRequired(),
    dataSource: stringType()
  })
    .shape({
      itemId: stringType(),
      bundleFlag: boolType(),
      dataSources: stringType(),
      identifiers: shapeType({
        productType: stringType()
      }),
      info: shapeType({
        classNumber: stringType(),
        hasSubscription: boolType(),
        isLiveGoodsProduct: boolType(),
        productDepartment: stringType(),
        productSubType: shapeType({
          name: stringType()
        }),
        globalCustomConfigurator: shapeType({
          customExperience: stringType()
        }),
        isBrioSku: boolType(),
        isCustomKitchenCabinet: boolType(),
        isGenericProduct: boolType()
      }),
      pricing: params({ storeId: stringType() })
        .shape({
          original: numberType({ float: true }),
          value: numberType({ float: true })
        }),
      availabilityType: shapeType({
        buyable: boolType(),
        discontinued: boolType(),
        status: boolType(),
        type: stringType()
      }),
      fulfillment: client(params({
        storeId: stringType(), zipCode: stringType()
      }).shape({
        anchorStoreStatus: boolType(),
        anchorStoreStatusType: stringType(),
        backordered: boolType(),
        backorderedShipDate: stringType(),
        bossExcludedShipStates: stringType(),
        sthExcludedShipState: stringType(),
        excludedShipStates: stringType(),
        seasonStatusEligible: boolType(),
        fulfillmentOptions: arrayOfType(shapeType({
          type: stringType(),
          fulfillable: boolType(),
          services: arrayOfType(Service),
          priorityDeliveryOptions: arrayOfType(shapeType({
            date: stringType(),
            timeline: stringType(),
            totalCharge: numberType({ float: true })
          })),
          programInclusions: arrayOfType(shapeType({
            description: stringType(),
            threshold: stringType()
          }))
        })),
        onlineStoreStatus: boolType(),
        onlineStoreStatusType: stringType()
      })),
      installServices
    }),
  checkAvailability: params({
    itemIds: arrayOfType(stringType().isRequired()).isRequired(),
    zipCode: stringType().isRequired(),
    localStoreId: stringType().isRequired()
  })
    .shape({
      fulfillmentOptions: arrayOfType(shapeType({
        fulfillable: boolType(),
        services: arrayOfType(
          shapeType({
            type: stringType(),
            isDefault: boolType(),
            earliestAvailabilityDate: stringType(),
            locations: arrayOfType(
              shapeType({
                locationId: stringType()
              })
            ),
            deliveryAvailabilityDetail: arrayOfType(shapeType({
              status: stringType(),
              itemId: stringType()
            }))
          })
        )
      }))
    }),
  shipping: deliveryDataModel.shipping
}, DeliveryOptions);

FulfillmentPodShipping.displayName = 'FulfillmentPodShipping';
